import {
  getSnapFilterById,
  hasActiveSnapLens,
} from "../snap/config/snapFilters";

const LENS_LOAD_PROBE_TIMEOUT_MS = 4000;
const SNAP_TOKEN_PLACEHOLDER = "PUT_STAGING_SNAP_API_TOKEN_HERE";
const PROBE_LENS_FILTER_ID = "glow_whitening";

let activeLensLoadAttemptId = 0;
let lensLoadProbeInFlight = false;
/** @type {{ cancelled: boolean } | null} */
let activeRunState = null;

function isSnapLensLoadProbeEnabled() {
  const raw = String(process.env.REACT_APP_SNAP_CALL_FILTER_LENS_LOAD_PROBE || "")
    .trim()
    .toLowerCase();
  return raw === "true";
}

function isProbeSnapTokenConfigured() {
  const token = String(process.env.REACT_APP_SNAP_API_TOKEN || "").trim();
  return Boolean(token && token !== SNAP_TOKEN_PLACEHOLDER);
}

function resolveProbeLensTarget() {
  const filter = getSnapFilterById(PROBE_LENS_FILTER_ID);
  if (!filter || !hasActiveSnapLens(filter)) {
    return null;
  }
  return {
    lensId: filter.lensId,
    lensGroupId: filter.lensGroupId,
  };
}

/**
 * Invalidate in-flight lens load probe (call end, cleanup, unmount).
 * @param {string} [_reason]
 */
export function invalidateSnapLensLoadProbe(_reason = "unknown") {
  activeLensLoadAttemptId += 1;
  if (activeRunState) {
    activeRunState.cancelled = true;
  }
  lensLoadProbeInFlight = false;
}

function isStaleLensLoadProbeAttempt(attemptId) {
  return attemptId !== activeLensLoadAttemptId;
}

function logLensLoadProbeAborted() {
  console.log("[snapLensLoadProbe] failed reason=aborted");
}

function throwIfLensLoadProbeCancelled(runState, attemptId) {
  if (runState.cancelled || isStaleLensLoadProbeAttempt(attemptId)) {
    throw Object.assign(new Error("aborted"), { code: "ABORTED" });
  }
}

/**
 * Bootstrap + loadLens probe — dynamic import when flag enabled.
 * Never throws; never touches Zego, session apply, camera, or snapCallFilter.
 * @param {{ callId?: string }} [_params]
 */
export async function runSnapLensLoadProbe(_params = {}) {
  try {
    const enabled = isSnapLensLoadProbeEnabled();
    console.log(`[snapLensLoadProbe] enabled=${enabled}`);
    if (!enabled) {
      return;
    }

    const tokenConfigured = isProbeSnapTokenConfigured();
    console.log(`[snapLensLoadProbe] tokenConfigured=${tokenConfigured}`);
    console.log(`[snapLensLoadProbe] selectedFilter=${PROBE_LENS_FILTER_ID}`);

    const lensTarget = resolveProbeLensTarget();
    const lensTargetResolved = Boolean(lensTarget);
    console.log(`[snapLensLoadProbe] lensTargetResolved=${lensTargetResolved}`);

    if (!tokenConfigured) {
      console.log("[snapLensLoadProbe] failed reason=missing_token");
      return;
    }

    if (!lensTargetResolved) {
      console.log("[snapLensLoadProbe] failed reason=lens_target_unresolved");
      return;
    }

    if (lensLoadProbeInFlight) {
      return;
    }

    const attemptId = activeLensLoadAttemptId;
    const runState = { cancelled: false };
    activeRunState = runState;
    lensLoadProbeInFlight = true;
    const startedAt = performance.now();
    console.log("[snapLensLoadProbe] started");

    const apiToken = String(process.env.REACT_APP_SNAP_API_TOKEN || "").trim();
    const { lensId, lensGroupId } = lensTarget;

    const operationPromise = (async () => {
      let cameraKit = null;
      try {
        let bootstrapCameraKit;
        try {
          ({ bootstrapCameraKit } = await import("@snap/camera-kit"));
        } catch {
          throw Object.assign(new Error("import_failed"), { code: "IMPORT_FAILED" });
        }
        throwIfLensLoadProbeCancelled(runState, attemptId);

        try {
          cameraKit = await bootstrapCameraKit({ apiToken, logLevel: "warn" });
        } catch {
          throw Object.assign(new Error("bootstrap_error"), { code: "BOOTSTRAP_ERROR" });
        }
        throwIfLensLoadProbeCancelled(runState, attemptId);

        try {
          await cameraKit.lensRepository.loadLens(lensId, lensGroupId);
        } catch {
          throw Object.assign(new Error("lens_load_failed"), { code: "LENS_LOAD_FAILED" });
        }
        throwIfLensLoadProbeCancelled(runState, attemptId);
      } finally {
        if (cameraKit) {
          try {
            await cameraKit.destroy?.();
          } catch {
            // Probe cleanup must never throw.
          }
        }
      }
    })();

    let timeoutId;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        runState.cancelled = true;
        reject(Object.assign(new Error("timeout"), { code: "TIMEOUT" }));
      }, LENS_LOAD_PROBE_TIMEOUT_MS);
    });

    try {
      await Promise.race([operationPromise, timeoutPromise]);
      operationPromise.catch(() => {});

      if (isStaleLensLoadProbeAttempt(attemptId) || runState.cancelled) {
        logLensLoadProbeAborted();
        return;
      }

      const ms = Math.round(performance.now() - startedAt);
      console.log(`[snapLensLoadProbe] success ms=${ms}`);
    } catch (err) {
      operationPromise.catch(() => {});

      if (err?.code === "TIMEOUT") {
        console.log(`[snapLensLoadProbe] timeout ms=${LENS_LOAD_PROBE_TIMEOUT_MS}`);
        return;
      }

      if (err?.code === "ABORTED" || isStaleLensLoadProbeAttempt(attemptId) || runState.cancelled) {
        logLensLoadProbeAborted();
        return;
      }

      if (err?.code === "IMPORT_FAILED") {
        console.log("[snapLensLoadProbe] failed reason=import_failed");
        return;
      }

      if (err?.code === "BOOTSTRAP_ERROR") {
        console.log("[snapLensLoadProbe] failed reason=bootstrap_error");
        return;
      }

      if (err?.code === "LENS_LOAD_FAILED") {
        console.log("[snapLensLoadProbe] failed reason=lens_load_failed");
        return;
      }

      console.log("[snapLensLoadProbe] failed reason=lens_load_failed");
    } finally {
      clearTimeout(timeoutId);
      if (activeRunState === runState) {
        activeRunState = null;
      }
      lensLoadProbeInFlight = false;
    }
  } catch {
    lensLoadProbeInFlight = false;
    activeRunState = null;
  }
}
